import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import Pill from '@pelckmans/elementis/components/pill';
import { useBreakpoint } from '@pelckmans/elementis/contexts/BreakpointsContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import AccountContext from '../../../contexts/AccountContext';
import IdentityContext from '../../../contexts/IdentityContext';
import { capitalize } from '../../../utils/string';
import { useRoutes } from '../../../hooks/useRoutes';
import { useIanuaLogout } from '../../../hooks/useLogout';
import useStudioGroup from '../../../hooks/useStudioGroup';
import useLicenses from '../../../hooks/useLicenses';

type UserMenuProps = {
  referenceEl: HTMLElement | null;
  actionEl: HTMLElement | null;
  onCollapse: (event?: Event) => void;
};

function UserMenu({ referenceEl, actionEl, onCollapse }: UserMenuProps) {
  const { t } = useTranslation();
  const {
    account: { role, firstName, lastName },
  } = useContext(AccountContext);
  const { identity } = useContext(IdentityContext);
  const { hasGroupFeatures } = useLicenses();

  const logout = useIanuaLogout();

  const { buildAbsolutePath } = useRoutes();

  const breakpoint = useBreakpoint();

  const studioGroup = useStudioGroup();
  const isCodeBased = studioGroup === 'BAO' || studioGroup === 'VO';
  const showGroupsAndProfile = identity.isTeacherAlike || !isCodeBased;

  return (
    <DropdownMenu
      actionEl={actionEl as HTMLButtonElement}
      referenceEl={referenceEl}
      onCollapse={onCollapse}
      menuProps={{ ariaLabel: t('APP_NAV.SIDE_NAV.USER_MENU.A11Y_LABEL'), id: 'user-menu' }}
      role="menu"
      placement={breakpoint === 'mobile' ? 'bottom-end' : 'right-end'}
      offset={20}
      grow
      showSeparators={false}
      selectableByEnter
    >
      {showGroupsAndProfile && (
        <DropdownMenu.Item
          as="link"
          href={buildAbsolutePath(['MY_PROFILE'])}
          value={null}
          role="menuitem"
          text={`${firstName} ${lastName}`.trim()}
          subText={t('APP_NAV.SIDE_NAV.USER_MENU.EDIT')}
          pillGroupProps={{
            role: 'group',
            'aria-label': undefined,
            children: <Pill key={1}>{capitalize(t(role))}</Pill>,
          }}
        />
      )}
      <DropdownMenu.Item
        as="link"
        href={buildAbsolutePath(['MY_METHODS'])}
        value={null}
        role="menuitem"
        text={t('APP_NAV.SIDE_NAV.USER_MENU.METHODS')}
        showSeparator
      />

      {identity.isTeacherAlike && hasGroupFeatures() && (
        <DropdownMenu.Item
          as="link"
          href={buildAbsolutePath(['MY_GROUPS'])}
          value={null}
          role="menuitem"
          text={t('APP_NAV.SIDE_NAV.USER_MENU.MY_GROUPS')}
          showSeparator={false}
        />
      )}

      {!identity.isTeacherAlike && showGroupsAndProfile && (
        <DropdownMenu.Item
          as="link"
          href={buildAbsolutePath(['MY_TEACHERS'])}
          value={null}
          role="menuitem"
          text={t('APP_NAV.SIDE_NAV.USER_MENU.MY_TEACHERS')}
          showSeparator={false}
        />
      )}

      <DropdownMenu.Item
        as="link"
        href={buildAbsolutePath(['HELP'])}
        value={null}
        role="menuitem"
        text={t('APP_NAV.SIDE_NAV.USER_MENU.HELP')}
      />
      <DropdownMenu.Item
        showSeparator
        onClick={logout}
        value={null}
        role="menuitem"
        text={t('APP_NAV.SIDE_NAV.USER_MENU.SIGN_OUT')}
        prominence="danger"
      />
    </DropdownMenu>
  );
}

export default UserMenu;
